import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFileExcel, FaFilePdf, FaPrint } from 'react-icons/fa';
import { ProductReportsTable } from './ProductReportsTableFilter';
import ProductFilter from '../common/ProductFilter';
import { useCurrent, useDelete, usePost } from '../../hooks';
import MwSpinner from './../ui/MwSpinner';
import { NoDataFound } from '../ui';
import handleConvertToPDF from '../../utils/handleConvertToPDF';
import handleConvertToPDF_V2 from '../../utils/handleConvertToPDF_V2';
 

export const ProductReportComponentFilter = () => {
    const [t, i18n] = useTranslation('global');
    const isRTL = i18n.dir() === 'rtl';
    const divPrintRef = useRef(null);
    const [isPrintMode, setIsPrintMode] = useState(false);
    
    // حالات البيانات
    const [products, setProducts] = useState([]);
    const [productsFilter, setProductsFilter] = useState([]);
    const [reportData, setReportData] = useState({ data: [] });
    const [loadingProducts, setLoadingProducts] = useState(false);
    
    // حالات الصفحات
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [filteredByPanel, setFilteredByPanel] = useState(null);
    
    // حالة البحث
    const [term, setTerm] = useState('');
    
    // الهوكس للتعامل مع API
    const { currentTenantId, currentCompanyId, currentLangId } = useCurrent();
    const { data: postResult, loading: postLoading, error, postData } = usePost();
    const { data: deleteResult, loading: deleteLoading, deleteItem } = useDelete();
    
    // حساب الصفحات
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = itemsPerPage === 20 
      ? productsFilter?.slice(indexOfFirstItem, indexOfLastItem) 
      : productsFilter;
    const totalPages = Math.ceil((productsFilter?.length || 0) / itemsPerPage);
    
    // جلب البيانات
    const getProducts = () => {
      postData(`${process.env.REACT_APP_PRODUCTS_API_SERVER_BASE_URL}/search/All`, {
        tenantId: currentTenantId,
        companyId: currentCompanyId
      });
    };
    
    // استخراج النص من كائنات اللغة
    const extractTextFromLanguageObject = (langObj) => {
      if (!langObj) return '';
      
      // إذا كان نصاً مباشراً
      if (typeof langObj === 'string') return langObj;
      
      // إذا كان مصفوفة من كائنات اللغة
      if (Array.isArray(langObj)) {
        const currentLang = i18n.language;
        const defaultLang = 'ar';
        
        // محاولة العثور على النص باللغة الحالية
        const textInCurrentLang = langObj.find(item => item.lang === currentLang);
        if (textInCurrentLang && textInCurrentLang.text) return textInCurrentLang.text;
        
        // أو باللغة الافتراضية
        const textInDefaultLang = langObj.find(item => item.lang === defaultLang);
        if (textInDefaultLang && textInDefaultLang.text) return textInDefaultLang.text;
        
        // أو أول عنصر
        if (langObj[0] && langObj[0].text) return langObj[0].text;
      }
      
      // إذا كان كائن لغة واحد
      if (langObj.text) return langObj.text;
      
      return '';
    };
    
    // تحويل البيانات وتنظيفها قبل العرض
    const processProductData = (productsList) => {
      if (!productsList || !Array.isArray(productsList)) return [];
      
      return productsList.map(product => {
        const currentLang = i18n.language;
        const defaultLang = 'ar';
        
        // معالجة اسم المنتج
        let productName = '';
        if (typeof product.productName === 'string') {
          productName = product.productName;
        } else if (Array.isArray(product.productName)) {
          const nameObj = product.productName.find(n => n.lang === currentLang) || 
                          product.productName.find(n => n.lang === defaultLang) || 
                          product.productName[0];
          productName = nameObj?.text || '';
        }
        
        // معالجة العلامة التجارية
        let brandName = '';
        if (product.productBrand) {
          if (typeof product.productBrand === 'string') {
            brandName = product.productBrand;
          } else if (product.productBrand.brandName) {
            if (Array.isArray(product.productBrand.brandName)) {
              const brandObj = product.productBrand.brandName.find(b => b.lang === currentLang) ||
                               product.productBrand.brandName.find(b => b.lang === defaultLang) ||
                               product.productBrand.brandName[0];
              brandName = brandObj?.text || '';
            } else if (typeof product.productBrand.brandName === 'string') {
              brandName = product.productBrand.brandName;
            }
          }
        }
        
        // معالجة الفئة
        let categoryName = '';
        if (product.productCategory && Array.isArray(product.productCategory) && product.productCategory.length > 0) {
          const category = product.productCategory[0];
          if (category.categoryName) {
            if (Array.isArray(category.categoryName)) {
              const catObj = category.categoryName.find(c => c.lang === currentLang) ||
                             category.categoryName.find(c => c.lang === defaultLang) ||
                             category.categoryName[0];
              categoryName = catObj?.text || '';
            } else if (typeof category.categoryName === 'string') {
              categoryName = category.categoryName;
            }
          }
        }
        
        // إرجاع الكائن المعالج
        return {
          ...product,
          productName: productName,
          brandName: brandName,
          categoryName: categoryName,
          // تأكد من أن جميع الحقول الأخرى المستخدمة في العرض هي قيم نصية
          productCode: product.productCode || '',
          price: product.price || 0,
          quantity: product.quantity || 0,
          productType: product.productType || ''
        };
      });
    };
    
    // جلب البيانات عند تحميل المكون
    useEffect(() => {
      getProducts();
    }, []);
    
    // تحديث البيانات عند استجابة API
    useEffect(() => {
      if (postResult) {
        let rawData;
        if (postResult.data) {
          rawData = postResult.data;
        } else {
          rawData = postResult;
        }
        
        // معالجة البيانات قبل الاستخدام
        const processedData = processProductData(rawData);
        setProducts(processedData);
        setProductsFilter(processedData);
      }
    }, [postResult]);
    
    // فرز البيانات
    useEffect(() => {
      if (products && products.length > 0) {
        const sortedData = [...products].sort((a, b) => {
          const codeA = parseInt(a.productCode) || a.productCode;
          const codeB = parseInt(b.productCode) || b.productCode;
          
          if (typeof codeA === 'number' && typeof codeB === 'number') {
            return codeA - codeB;  
          } else {
            return String(codeA).localeCompare(String(codeB));  
          }
        });
        
        setProducts(sortedData);
        setProductsFilter(sortedData);
      }
    }, [products.length]);
    
    // تحديث reportData بناء على productsFilter
    useEffect(() => {
      setReportData({ data: productsFilter });
    }, [productsFilter]);
    
    // وظيفة معالجة البحث
    const onChangeHandler = () => {
      setCurrentPage(1);
      
      // تحديد المنتجات المراد البحث فيها (إما جميع المنتجات أو المنتجات المفلترة)
      const productsToSearch = filteredByPanel || products;
      
      if (term) {
        const searchTerm = term.toString().toLowerCase().trim();
        const _list = productsToSearch?.filter((product) => {
          const productName = product.productName.toString().toLowerCase();
          const productDesc = (product.productDesc || '').toString().toLowerCase();
          const brandName = (product.brandName || '').toString().toLowerCase();
          const categoryName = (product.categoryName || '').toString().toLowerCase();
          const productCode = (product.productCode || '').toString().toLowerCase();
          
          return productName.includes(searchTerm) || 
                 productDesc.includes(searchTerm) || 
                 brandName.includes(searchTerm) || 
                 categoryName.includes(searchTerm) || 
                 productCode.includes(searchTerm);
        });
        setProductsFilter(_list);
      } else {
        setProductsFilter(productsToSearch);
      }
      setLoadingProducts(false);
    };
    
    // تعامل مع تغييرات مصطلح البحث
    useEffect(() => {
      if (!term && term !== '') {
        setProductsFilter(filteredByPanel || products);
        setLoadingProducts(false);
        return;
      }
      
      setLoadingProducts(true);
      const timeout = setTimeout(() => {
        onChangeHandler();
      }, 500);
      
      return () => {
        clearTimeout(timeout);
      };
    }, [term, filteredByPanel, products]);
    
    // التعامل مع تغييرات الفلتر
    useEffect(() => {
      if (filteredByPanel === null) {
        // لا توجد مرشحات نشطة، استخدم جميع المنتجات
        if (!term) {
          setProductsFilter(products);
        } else {
          onChangeHandler();
        }
      } else {
        // مرشحات نشطة، استخدم المنتجات المفلترة
        if (!term) {
          setProductsFilter(filteredByPanel);
        } else {
          // إعادة تشغيل البحث على المنتجات المفلترة
          onChangeHandler();
        }
      }
    }, [filteredByPanel]);
    
    // وظائف الزر
    const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
    };
    
    const exportExcel = () => {
      try {
        // استيراد مكتبة xlsx لتصدير البيانات إلى Excel
        import('xlsx').then((XLSX) => {
          // استخدام البيانات المفلترة الحالية (productsFilter) التي تظهر للمستخدم
          // إنشاء مصفوفة جديدة تحتوي فقط على البيانات المطلوبة من البيانات المفلترة
          const dataToExport = productsFilter.map(product => ({
            "الكود": product.productCode,
            "اسم المنتج": product.productName,
            "السعر": product.price,
            "الكمية": product.quantity,
            "التصنيف": product.categoryName
          }));
    
          // إنشاء ورقة عمل جديدة
          const worksheet = XLSX.utils.json_to_sheet(dataToExport);
          
          // إنشاء مصنف جديد وإضافة ورقة العمل إليه
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "تقرير المنتجات");
          
          // تصدير المصنف كملف Excel
          XLSX.writeFile(workbook, "تقرير_المنتجات.xlsx");
        });
      } catch (error) {
        console.error("حدث خطأ أثناء تصدير البيانات:", error);
      }
    };
    const pdfHandler = () => {
      // استخدام وظيفة handleConvertToPDF
      handleConvertToPDF_V2({
        lang: i18n.language, // استخدام اللغة الحالية
        elementId: 'printableDiv', // استخدام العنصر الذي يحتوي على البيانات
        fileName: 'تقرير_المنتجات.pdf' // اسم الملف
      });
    };
    const printHandler = () => {
      setIsPrintMode(true);
      
      // إنشاء نافذة طباعة جديدة
      const printWindow = window.open('', '_blank');
      
      // الحصول على محتوى الجدول
      let tableHTML = "";
      if (divPrintRef.current) {
        // استخدام المكون ProductReportsTable مباشرة بدلاً من البحث عن الجدول
        tableHTML = reportData && reportData.data && reportData.data.length > 0 ? 
          `<table border="1" cellpadding="8" cellspacing="0" style="width:100%; border-collapse: collapse;">
            <thead>
              <tr>
                <th>${t("product.code")}</th>
                <th>${t("product.name")}</th>
                <th>${t("product.price")}</th>
                <th>${t("product.quantity")}</th>
                <th>${t("product.category")}</th>
              </tr>
            </thead>
            <tbody>
              ${reportData.data.map(product => `
                <tr>
                  <td>${product.productCode || ""}</td>
                  <td>${product.productName || ""}</td>
                  <td>${product.price || 0}</td>
                  <td>${product.quantity || 0}</td>
                  <td>${product.categoryName || ""}</td>
                </tr>
              `).join("")}
            </tbody>
          </table>` :
          `<div style="text-align:center; padding:20px;">${t("deleted.no_data")}</div>`;
      }
      
      // تحضير محتوى الطباعة - فقط العنوان والجدول
      const printContent = `
        <html>
          <head>
            <title>تقرير المنتجات</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                direction: ${isRTL ? 'rtl' : 'ltr'};
                text-align: ${isRTL ? 'right' : 'left'};
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
              }
              th {
                background-color: #f2f2f2;
              }
              .report-title {
                text-align: center;
                font-weight: bold;
                margin-bottom: 20px;
                font-size: 18px;
                padding: 10px;
              }
            </style>
          </head>
          <body>
            <div class="report-title">${t("reports.product_Report")}</div>
            ${tableHTML}
          </body>
        </html>
      `;
      
      // كتابة المحتوى في نافذة الطباعة
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // تشغيل الطباعة بعد تحميل المحتوى
      printWindow.onload = function() {
        printWindow.print();
        printWindow.onafterprint = function() {
          printWindow.close();
          setIsPrintMode(false);
        };
      };
    };
  
    return (
      <div className=" h-[calc(100vh-235px)] overflow-y-scroll custom-scrollbar">
        {/* عنوان التقرير وأدوات الفلترة */}
       
        {/* عرض الجدول والأدوات */}
        <div className="w-full">
          {postLoading ? (
            <div className="text-center py-4">     <MwSpinner/> </div>
          ) : error ? (
            <div className="text-center py-4 text-red-500">حدث خطأ أثناء تحميل البيانات</div>
          ) : (
            <>
              <div className="flex justify-between">
                <div className="px-2 text-[#251658]"></div>
                <div className="p-3 text-[#251658] flex justify-between w-full items-center">
                <div className={`flex ${isRTL ? 'left-24' : 'left-24'} mb-4`}>
                <ProductFilter 
                    isRTL={isRTL}

              products={products} 
              onFilterChange={(filtered) => setFilteredByPanel(filtered)}
            />
          </div>
                  <ul className="flex gap-1">
                    <li
                      style={{ cursor: "pointer" }}
                      onClick={exportExcel}
                      className="px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold"
                    >
                      <FaRegFileExcel />
                      <span className="text-sm text-[#251658]">{t("reports.Export_Excel")}</span>
                    </li>
                    <li
                      style={{ cursor: "pointer" }}
                      onClick={pdfHandler}
                      className="px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold"
                    >
                      <FaFilePdf />
                      <span className="text-sm text-[#251658]">{t("reports.Save_as_PDF")}</span>
                    </li>
                    <li
                      style={{ cursor: "pointer" }}
                      onClick={printHandler}
                      className="px-2 py-1 flex items-center gap-1 bg-white border border-gray-200 hover:border-[#251658] hover:font-bold"
                    >
                      <FaPrint />
                      <span className="text-sm text-[#251658]">{t("reports.Print")}</span>
                    </li>
                  </ul>

               
                </div>
              </div>
              <div
                id="printableDiv"
                ref={divPrintRef}
                className={`text-[#251658] ${isPrintMode ? "" : "h-[calc(100vh-100px)] overflow-y-scroll custom-scrollbar"}`}
              >
              <div className="text-[#251658] text-center mb-4">
                <div className="font-bold">{t("reports.Product_Data_Report")}</div>
              </div>
              {productsFilter && productsFilter.length > 0 ? (
                <ProductReportsTable reportData={reportData} />
              ) : (
                <div className="text-center py-8 text-[#251658]/70"><NoDataFound msg={t("deleted.no_data")}/>    </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};